import { z } from 'zod';

import { apiUrl } from '@/lib/env';
import { ApiError, fetchJson } from '@/lib/http';

/**
 * Схемы ответов API расшифровок.
 *
 * Опорный контракт — `@axium/contracts` и реальные ответы `apps/api`.
 *
 * `durationMs` и `fileSizeBytes` — BigInt-поля, приходят десятичной строкой и
 * такими же остаются: приводить их к `Number` запрещено правилами проекта.
 */

const nullableString = z.string().nullish();

const userRefSchema = z
  .object({
    id: z.string(),
    displayName: z.string(),
  })
  .passthrough();

const transcriptionResourceSchema = z
  .object({
    id: z.union([z.string().min(1), z.number()]),
    title: nullableString,
    originalFileName: z.string().optional(),
    sourceMimeType: z.string().optional(),
    sourceFileSize: z
      .string()
      .regex(/^\d+$/u)
      .optional()
      .default('0'),
    durationSeconds: z.number().int().nonnegative().nullable(),
    status: z.string(),
    processingProgress: z.number().int().min(0).max(100),
    errorCode: nullableString,
    errorMessage: nullableString,
    /** Этап, на котором произошла ошибка: MEDIA_PROCESSING | TRANSCRIPTION (нет — на старых записях). */
    failureStage: nullableString,
    mimeType: nullableString,
    meetingType: nullableString,
    meetingDate: nullableString,
    clientName: nullableString,
    clientParticipantNames: z.array(z.string()).optional().default([]),
    axiumParticipants: z.array(userRefSchema).optional().default([]),
    createdAt: nullableString,
    updatedAt: nullableString,
    completedAt: nullableString,
    createdById: z.string(),
    createdBy: userRefSchema.optional(),
    processingJobs: z
      .array(
        z.object({
          type: z.string(),
          status: z.string(),
          progress: z.number().int().min(0).max(100),
          startedAt: nullableString,
          finishedAt: nullableString,
          errorMessage: nullableString,
        }),
      )
      .optional()
      .default([]),
    latestChatGptResult: z
      .object({
        id: z.string(),
        title: z.string(),
        createdAt: z.string(),
      })
      .nullable()
      .optional(),
  })
  .passthrough();

type RawTranscription = z.infer<typeof transcriptionResourceSchema>;

export type LatestChatGptResultRef = {
  id: string;
  title: string;
  createdAt: string;
};

export type Transcription = {
  id: string;
  title: string | null;
  status: string | null;
  language: string | null;
  /** Целые миллисекунды десятичной строкой (BigInt в API). */
  durationMs: string | null;
  /** Байты десятичной строкой (BigInt в API). */
  fileSizeBytes: string | null;
  progress: number | null;
  failureStage: string | null;
  meetingType: string | null;
  meetingDate: string | null;
  clientName: string | null;
  clientParticipantNames: string[];
  axiumParticipants: Array<{ id: string; displayName: string }>;
  createdById: string | null;
  createdByName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  processingJobs: ProcessingStageJob[];
  latestChatGptResult: LatestChatGptResultRef | null;
};

export type ProcessingStageJob = {
  type: string;
  status: string;
  progress: number;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
};

function firstString(...values: readonly (string | null | undefined)[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }
  return null;
}

function normalizeTranscription(raw: RawTranscription): Transcription {
  return {
    id: String(raw.id),
    title: firstString(raw.title, raw.originalFileName),
    status: firstString(raw.status),
    language: null,
    durationMs: raw.durationSeconds === null ? null : String(raw.durationSeconds * 1000),
    fileSizeBytes: raw.sourceFileSize ?? null,
    progress: raw.processingProgress,
    failureStage: firstString(raw.failureStage),
    meetingType: firstString(raw.meetingType),
    meetingDate: firstString(raw.meetingDate),
    clientName: firstString(raw.clientName),
    clientParticipantNames: raw.clientParticipantNames ?? [],
    axiumParticipants: (raw.axiumParticipants ?? []).map((item) => ({
      id: item.id,
      displayName: item.displayName,
    })),
    createdById: firstString(raw.createdById),
    createdByName: firstString(raw.createdBy?.displayName),
    createdAt: firstString(raw.createdAt),
    updatedAt: firstString(raw.updatedAt),
    completedAt: firstString(raw.completedAt),
    errorMessage: firstString(raw.errorMessage),
    processingJobs: (raw.processingJobs ?? []).map((job) => ({
      type: job.type,
      status: job.status,
      progress: job.progress,
      startedAt: firstString(job.startedAt),
      finishedAt: firstString(job.finishedAt),
      errorMessage: firstString(job.errorMessage),
    })),
    latestChatGptResult:
      raw.latestChatGptResult === null || raw.latestChatGptResult === undefined
        ? null
        : {
            id: raw.latestChatGptResult.id,
            title: raw.latestChatGptResult.title,
            createdAt: raw.latestChatGptResult.createdAt,
          },
  };
}

export type TranscriptionsPage = {
  items: Transcription[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export const transcriptionsPageSchema = z
  .object({
    items: z.array(transcriptionResourceSchema),
    page: z.number(),
    pageSize: z.number(),
    total: z.number(),
    totalPages: z.number(),
  })
  .transform((payload): TranscriptionsPage => {
    return {
      items: payload.items.map(normalizeTranscription),
      page: payload.page,
      pageSize: payload.pageSize,
      total: payload.total,
      totalPages: payload.totalPages,
    };
  });

/**
 * Карточка записи — статус и метаданные обработки. Текст расшифровки и
 * резюме сюда не встраиваются: они грузятся отдельными запросами
 * (`fetchTranscriptMeta`, `fetchTranscriptSegments`), когда статус это позволяет.
 */
export type TranscriptionDetail = Transcription;

export const transcriptionDetailSchema = transcriptionResourceSchema.transform(normalizeTranscription);

export type TranscriptionListFilters = {
  search?: string;
  clientName?: string;
  axiumParticipantId?: string;
  createdById?: string;
  meetingType?: string;
  status?: string;
  page?: number;
  pageSize?: number;
};

function buildQuery(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value.trim() !== '') {
      search.set(key, value);
    }
  }
  const qs = search.toString();
  return qs === '' ? '' : `?${qs}`;
}

export function fetchTranscriptions(
  filters: TranscriptionListFilters = {},
  signal?: AbortSignal,
): Promise<TranscriptionsPage> {
  const query = buildQuery({
    search: filters.search,
    clientName: filters.clientName,
    axiumParticipantId: filters.axiumParticipantId,
    createdById: filters.createdById,
    meetingType: filters.meetingType,
    status: filters.status,
    page: filters.page !== undefined ? String(filters.page) : undefined,
    pageSize: filters.pageSize !== undefined ? String(filters.pageSize) : undefined,
  });
  return fetchJson(
    `/transcriptions${query}`,
    transcriptionsPageSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

export function fetchTranscription(id: string, signal?: AbortSignal): Promise<TranscriptionDetail> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(id)}`,
    transcriptionDetailSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

const transcriptionResponseSchema = transcriptionResourceSchema.transform(normalizeTranscription);

export type CreateTranscriptionInput = {
  title: string;
  originalFileName: string;
  sourceMimeType: string;
  /** Байты десятичной строкой (BigInt в API). */
  sourceFileSize: string;
  meetingType: 'PRE_SALE' | 'CLIENT_WORK' | 'INTERNAL';
  meetingDate: string;
  clientName?: string | null;
  axiumParticipantIds: string[];
  clientParticipantNames: string[];
};

const directoryUsersSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      displayName: z.string(),
      email: z.string(),
      authProvider: z.string(),
    }),
  ),
  source: z.enum(['local', 'bitrix24']),
});

export type DirectoryUser = z.infer<typeof directoryUsersSchema>['items'][number];

export function fetchDirectoryUsers(signal?: AbortSignal): Promise<DirectoryUser[]> {
  return fetchJson('/users', directoryUsersSchema, signal !== undefined ? { signal } : undefined).then(
    (payload) => payload.items,
  );
}

const clientSuggestionsSchema = z.object({
  items: z.array(z.string()),
});

export function fetchClientSuggestions(
  q?: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const query = buildQuery({ q, limit: '20' });
  return fetchJson(
    `/transcriptions/clients${query}`,
    clientSuggestionsSchema,
    signal !== undefined ? { signal } : undefined,
  ).then((payload) => payload.items);
}

export function createTranscription(
  input: CreateTranscriptionInput,
  signal?: AbortSignal,
): Promise<Transcription> {
  return fetchJson('/transcriptions', transcriptionResponseSchema, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    ...(signal !== undefined ? { signal } : {}),
  });
}

export type UpdateMeetingInput = {
  title: string;
  meetingType: 'PRE_SALE' | 'CLIENT_WORK' | 'INTERNAL';
  meetingDate: string;
  clientName?: string | null;
  axiumParticipantIds: string[];
  clientParticipantNames: string[];
};

export function updateMeeting(
  id: string,
  input: UpdateMeetingInput,
  signal?: AbortSignal,
): Promise<Transcription> {
  return fetchJson(`/transcriptions/${encodeURIComponent(id)}`, transcriptionResponseSchema, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    ...(signal !== undefined ? { signal } : {}),
  });
}

const uploadSessionResponseSchema = z.object({
  uploadSessionId: z.string().min(1),
  uploadToken: z.string().min(1),
  /** Абсолютный URL или same-origin путь `/files/` (через nginx/web-прокси). */
  tusEndpoint: z
    .string()
    .min(1)
    .refine(
      (value) => {
        const trimmed = value.trim();
        if (trimmed.startsWith('/')) {
          return /^\/[\w./-]*$/.test(trimmed);
        }
        try {
          const parsed = new URL(trimmed);
          return parsed.protocol === 'http:' || parsed.protocol === 'https:';
        } catch {
          return false;
        }
      },
      { message: 'Ожидается абсолютный URL или путь от корня (/files/)' },
    ),
  expiresAt: z.string(),
});

export type UploadSession = z.infer<typeof uploadSessionResponseSchema>;

export type CreateUploadSessionInput = {
  fileName: string;
  fileSize: number | string;
  mimeType?: string;
};

export function createUploadSession(
  transcriptionId: string,
  input: CreateUploadSessionInput,
  signal?: AbortSignal,
): Promise<UploadSession> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/upload-session`,
    uploadSessionResponseSchema,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      ...(signal !== undefined ? { signal } : {}),
    },
  );
}

export type ConfirmUploadCompleteInput = {
  uploadSessionId: string;
  tusUploadUrl?: string;
};

export function confirmUploadComplete(
  transcriptionId: string,
  input: ConfirmUploadCompleteInput,
  signal?: AbortSignal,
): Promise<unknown> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/upload-complete`,
    z.unknown(),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      ...(signal !== undefined ? { signal } : {}),
    },
  );
}

const playerUrlSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string(),
});

export type PlayerUrl = z.infer<typeof playerUrlSchema>;

export function fetchPlayerUrl(transcriptionId: string, signal?: AbortSignal): Promise<PlayerUrl> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/player-url`,
    playerUrlSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

export function retryMediaProcessing(
  transcriptionId: string,
  signal?: AbortSignal,
): Promise<unknown> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/retry-media-processing`,
    z.unknown(),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      ...(signal !== undefined ? { signal } : {}),
    },
  );
}

/**
 * Публичные метаданные завершённой расшифровки (без слов, только сводка).
 * Доступны, когда статус — TRANSCRIBED/READY.
 */
export const transcriptMetaSchema = z.object({
  text: z.string().optional(),
  detectedLanguage: nullableString,
  languageProbability: z.number().min(0).max(1).nullable(),
  segmentsCount: z.number().int().nonnegative(),
  wordsCount: z.number().int().nonnegative(),
  completedAt: z.string(),
  model: z.string(),
});

export type TranscriptMeta = z.infer<typeof transcriptMetaSchema>;

export function fetchTranscriptMeta(id: string, signal?: AbortSignal): Promise<TranscriptMeta> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(id)}/transcript`,
    transcriptMetaSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

export const transcriptSegmentItemSchema = z.object({
  id: z.string(),
  index: z.number().int().nonnegative(),
  startMs: z.number().int().nonnegative(),
  endMs: z.number().int().nonnegative(),
  text: z.string(),
  /** Есть только после опубликованной диаризации; в UI спикеров не показываем. */
  speaker: z
    .object({
      id: z.string(),
      index: z.number().int().nonnegative(),
      displayName: z.string(),
    })
    .nullable()
    .optional(),
  hasMultipleSpeakers: z.boolean().optional(),
});

export type TranscriptSegmentItem = z.infer<typeof transcriptSegmentItemSchema>;

export const transcriptSegmentsPageSchema = z.object({
  items: z.array(transcriptSegmentItemSchema),
  nextCursor: z.string().nullable(),
});

export type TranscriptSegmentsPage = z.infer<typeof transcriptSegmentsPageSchema>;

export type FetchTranscriptSegmentsParams = {
  cursor?: string;
  limit?: number;
};

/** Сегменты грузятся страницами через cursor — тысячи строк за раз не запрашиваем. */
export function fetchTranscriptSegments(
  id: string,
  params: FetchTranscriptSegmentsParams = {},
  signal?: AbortSignal,
): Promise<TranscriptSegmentsPage> {
  const search = new URLSearchParams();
  if (params.cursor !== undefined && params.cursor !== '') {
    search.set('cursor', params.cursor);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  const query = search.toString();
  return fetchJson(
    `/transcriptions/${encodeURIComponent(id)}/transcript/segments${query !== '' ? `?${query}` : ''}`,
    transcriptSegmentsPageSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

/** Все сегменты записи постранично — для полного копирования, независимо от UI. */
export async function fetchAllTranscriptSegments(
  id: string,
  signal?: AbortSignal,
): Promise<TranscriptSegmentItem[]> {
  const items: TranscriptSegmentItem[] = [];
  let cursor: string | null = null;

  for (;;) {
    const page: TranscriptSegmentsPage = await fetchTranscriptSegments(
      id,
      {
        limit: 200,
        ...(cursor !== null ? { cursor } : {}),
      },
      signal,
    );
    items.push(...page.items);
    if (page.nextCursor === null || page.nextCursor === '') {
      break;
    }
    cursor = page.nextCursor;
  }

  return items;
}

export function retryTranscription(id: string, signal?: AbortSignal): Promise<unknown> {
  return fetchJson(`/transcriptions/${encodeURIComponent(id)}/retry-transcription`, z.unknown(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    ...(signal !== undefined ? { signal } : {}),
  });
}

export const diarizationSpeakerSchema = z.object({
  id: z.string(),
  index: z.number().int().nonnegative(),
  label: z.string().optional(),
  displayName: z.string(),
  totalSpeechMs: z.number().int().nonnegative(),
  snippets: z
    .array(
      z.object({
        startMs: z.number().int().nonnegative(),
        endMs: z.number().int().nonnegative(),
        text: z.string(),
      }),
    )
    .optional()
    .default([]),
});

export const diarizationMetaSchema = z.object({
  speakersCount: z.number().int().nonnegative(),
  turnsCount: z.number().int().nonnegative(),
  utterancesCount: z.number().int().nonnegative(),
  assignedWordsCount: z.number().int().nonnegative(),
  unassignedWordsCount: z.number().int().nonnegative(),
  model: z.string(),
  modelRevision: z.string().nullable().optional(),
  completedAt: z.string(),
  speakers: z.array(diarizationSpeakerSchema),
});

export type DiarizationMeta = z.infer<typeof diarizationMetaSchema>;

export function fetchDiarizationMeta(id: string, signal?: AbortSignal): Promise<DiarizationMeta> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(id)}/diarization`,
    diarizationMetaSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

export const diarizedUtteranceItemSchema = z.object({
  id: z.string(),
  index: z.number().int().nonnegative(),
  startMs: z.number().int().nonnegative(),
  endMs: z.number().int().nonnegative(),
  text: z.string(),
  speaker: diarizationSpeakerSchema.nullable(),
});

export type DiarizedUtteranceItem = z.infer<typeof diarizedUtteranceItemSchema>;

export const diarizedUtterancesPageSchema = z.object({
  items: z.array(diarizedUtteranceItemSchema),
  nextCursor: z.string().nullable(),
});

export type DiarizedUtterancesPage = z.infer<typeof diarizedUtterancesPageSchema>;

export type FetchDiarizedUtterancesParams = {
  cursor?: string;
  limit?: number;
};

export function fetchDiarizedUtterances(
  id: string,
  params: FetchDiarizedUtterancesParams = {},
  signal?: AbortSignal,
): Promise<DiarizedUtterancesPage> {
  const search = new URLSearchParams();
  if (params.cursor !== undefined && params.cursor !== '') {
    search.set('cursor', params.cursor);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  const query = search.toString();
  return fetchJson(
    `/transcriptions/${encodeURIComponent(id)}/diarization/segments${query !== '' ? `?${query}` : ''}`,
    diarizedUtterancesPageSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

export function retryDiarization(id: string, force = false, signal?: AbortSignal): Promise<unknown> {
  const query = force ? '?force=true' : '';
  return fetchJson(`/transcriptions/${encodeURIComponent(id)}/retry-diarization${query}`, z.unknown(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    ...(signal !== undefined ? { signal } : {}),
  });
}

const purgeResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

/** Полное удаление встречи (очередь MEETING_DELETE). Доступно владельцу и ADMIN. */
export function purgeTranscription(id: string, signal?: AbortSignal) {
  return fetchJson(`/transcriptions/${encodeURIComponent(id)}/purge`, purgeResponseSchema, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    ...(signal !== undefined ? { signal } : {}),
  });
}

export type AssignSpeakerInput =
  | { axiumParticipantId: string }
  | { clientParticipantName: string }
  | { customName: string }
  | { markAsUnknown: true };

export function assignSpeaker(
  transcriptionId: string,
  speakerId: string,
  input: AssignSpeakerInput,
  signal?: AbortSignal,
): Promise<z.infer<typeof diarizationSpeakerSchema>> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/speakers/${encodeURIComponent(speakerId)}/assign`,
    diarizationSpeakerSchema,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
      ...(signal !== undefined ? { signal } : {}),
    },
  );
}

const chatGptPromptIdSchema = z.enum([
  'summary',
  'presale',
  'client_work',
  'internal',
  'followup',
  'action_items',
  'meeting_protocol',
  'custom',
  'other',
]);

const chatGptResultBaseSchema = z.object({
  id: z.string(),
  transcriptionId: z.string(),
  promptId: chatGptPromptIdSchema,
  promptLabel: z.string(),
  title: z.string(),
  createdById: z.string(),
  createdBy: userRefSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const chatGptResultSummarySchema = chatGptResultBaseSchema;

export const chatGptResultSchema = chatGptResultBaseSchema.extend({
  contentHtml: z.string(),
  contentText: z.string(),
});

export type ChatGptResultSummary = z.infer<typeof chatGptResultSummarySchema>;
export type ChatGptResult = z.infer<typeof chatGptResultSchema>;
export type ChatGptPromptId = z.infer<typeof chatGptPromptIdSchema>;

const listChatGptResultsSchema = z.object({
  items: z.array(chatGptResultSummarySchema),
});

export type CreateChatGptResultInput = {
  promptId: ChatGptPromptId;
  promptLabel: string;
  title: string;
  contentHtml: string;
};

export type UpdateChatGptResultInput = {
  promptId?: ChatGptPromptId;
  promptLabel?: string;
  title?: string;
  contentHtml?: string;
};

export function listChatGptResults(
  transcriptionId: string,
  signal?: AbortSignal,
): Promise<{ items: ChatGptResultSummary[] }> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/chatgpt-results`,
    listChatGptResultsSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

export function fetchChatGptResult(
  transcriptionId: string,
  resultId: string,
  signal?: AbortSignal,
): Promise<ChatGptResult> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/chatgpt-results/${encodeURIComponent(resultId)}`,
    chatGptResultSchema,
    signal !== undefined ? { signal } : undefined,
  );
}

export function createChatGptResult(
  transcriptionId: string,
  body: CreateChatGptResultInput,
  signal?: AbortSignal,
): Promise<ChatGptResult> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/chatgpt-results`,
    chatGptResultSchema,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(signal !== undefined ? { signal } : {}),
    },
  );
}

export function updateChatGptResult(
  transcriptionId: string,
  resultId: string,
  body: UpdateChatGptResultInput,
  signal?: AbortSignal,
): Promise<ChatGptResult> {
  return fetchJson(
    `/transcriptions/${encodeURIComponent(transcriptionId)}/chatgpt-results/${encodeURIComponent(resultId)}`,
    chatGptResultSchema,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(signal !== undefined ? { signal } : {}),
    },
  );
}

export function deleteChatGptResult(
  transcriptionId: string,
  resultId: string,
  signal?: AbortSignal,
): Promise<void> {
  return (async () => {
    const path = `/transcriptions/${encodeURIComponent(transcriptionId)}/chatgpt-results/${encodeURIComponent(resultId)}`;
    const response = await fetch(apiUrl(path), {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(signal !== undefined ? { signal } : {}),
    });
    if (!response.ok) {
      if (response.status === 401 && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
        return new Promise(() => {});
      }
      const message = await response.text().catch(() => response.statusText);
      throw new ApiError(message || 'Не удалось удалить результат', response.status);
    }
  })();
}
